<template>
  <div class="flex flex-col h-full">
    <div class="flex-1 video-player">
      <video ref="videoRef" class="video-element" :src="store.videoFileUrl" :muted="isMuted"
        @loadedmetadata="handleMetadata" @timeupdate="handleTimeUpdate" @ended="handleEnded"
        :controls="props?.videoAttributes?.controls">
      </video>

      <!-- 视频加载遮罩 -->
      <div v-if="loading" class="video-loader">
        <div class="loader-spinner"></div>
      </div>

    </div>

    <div class="flex relative justify-between py-1 video-controls">
      <div class="flex  justify-between items-center w-[120px]">
        <button class="control-btn" @click="emit('toggle-play')">
          <v-icon class="text-3xl" :icon="props.isPlaying ? 'mdi-pause' : 'mdi-play'" />
        </button>
        <div class="flex items-center">
          <span class="time">{{ formattedCurrentTime }} /</span>
          <span class="text-neutral-400 time">{{ formattedDuration }}</span>
        </div>
      </div>
      <button class="flex items-center" @click="toggleMute">
        <v-icon class="mute-btn" :icon="videoRef?.muted ? 'mdi-volume-off' : 'mdi-volume-high'" />
      </button>

    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount } from 'vue'
import { formatTimeOnlyHoursAndMinutes } from './utils';
import { useVideoEditorStore } from './store';

const props = withDefaults(defineProps<{
  src: string | null,
  isPlaying: boolean,
  videoAttributes?: {
    controls: boolean
  }
}>(), {
  src: null,
  isPlaying: false,
  videoAttributes: () => ({
    controls: false,
  })
})
props
const emit = defineEmits<{
  'duration-change': [duration: number]
  'time-update': [time: number]
  'toggle-play': []
}>()

const videoRef = ref<HTMLVideoElement>()
const loading = ref(true)
const store = useVideoEditorStore()
const isMuted = ref(false)


onMounted(() => {
  if (videoRef.value) {
    store.setVideoRef(videoRef.value)
  }
})


onBeforeUnmount(() => {
  store.setVideoRef(null)
  if (store.videoFileUrl) {
    URL.revokeObjectURL(store.videoFileUrl)
  }
})

watch(() => props.src, (newSrc) => {
  if (newSrc) {
    store.setVideoFileUrl(newSrc)
  } else {
    store.setVideoFileUrl(null)
  }
})

onBeforeUnmount(() => {
  if (store.videoFileUrl) {
    URL.revokeObjectURL(store.videoFileUrl)
  }
})

const handleMetadata = () => {
  if (videoRef.value) {
    store.setDuration(videoRef.value.duration)
    loading.value = false
  }
}

const toggleMute = () => {
  if (videoRef.value) {
    isMuted.value = !isMuted.value
    videoRef.value.muted = isMuted.value
  }
}

const handleTimeUpdate = () => {
  if (videoRef.value) {
    //videoRef.value.currentTime 视频播放的当前时间，单位是秒
    // store.setCurrentTime(videoRef.value.currentTime)
    // emit('time-update', videoRef.value.currentTime)
    requestAnimationFrame(() => {
      if (videoRef.value) {
        store.setCurrentTime(videoRef.value.currentTime)
        emit('time-update', videoRef.value.currentTime)
      }
    })
  }
}

const handleEnded = () => {
  store.setIsPlaying(false)
  // 重置到开始时间
  if (videoRef.value) {
    console.log('reset to start time', store.startTime)
    videoRef.value.currentTime = store.startTime / 1000000
    store.setCurrentTime(store.startTime)
  }
  emit('toggle-play')
}

const formattedCurrentTime = computed(() => formatTimeOnlyHoursAndMinutes(store.currentTime))
const formattedDuration = computed(() => formatTimeOnlyHoursAndMinutes(store.duration))

defineExpose({
  play: async () => {
    if (videoRef.value) {
      await videoRef.value.play()
    }
  },
  pause: async () => {
    if (videoRef.value) {
      await videoRef.value.pause()
    }
  }
})
</script>

<style scoped>
.video-player {
  position: relative;
  flex: 1;
  width: 100%;
  background: black;
  border-radius: 8px;
  overflow: hidden;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.loader-spinner {
  border: 3px solid #F06906;
  border-top: 3px solid #F06906;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.current-time-display {
  position: absolute;
  bottom: 8px;
  left: 8px;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px;
  border-radius: 4px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.video-controls {
  /* padding: 10px; */
  /* background: rgba(0, 0, 0, 0.5); */
  gap: 8px;
}

.control-btn {
  background: none;
  border: none;
  color: #A3A3A3;
  cursor: pointer;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
}

.progress {
  height: 100%;
  background: #F06906;
  border-radius: 2px;
}

.mute-btn {
  background: none;
  border: none;
  color: hsla(0, 0%, 100%, .7);
  cursor: pointer;
}

.time {
  color: hsla(0, 0%, 100%, .7);
  font-size: 14px;
}
</style>